import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';

// Lazy-initialized clients to ensure Amplify.configure() has been called first
let _client: ReturnType<typeof generateClient<Schema>> | null = null;
let _publicClient: ReturnType<typeof generateClient<Schema>> | null = null;

/**
 * Type-safe Amplify Data client singleton (default auth - userPool)
 * Lazily initialized to ensure Amplify is configured first.
 *
 * Usage:
 *   import { client } from '@/services/amplifyClient';
 *   const { data, errors } = await client.models.User.list();
 */
export const client = new Proxy({} as ReturnType<typeof generateClient<Schema>>, {
  get(_target, prop) {
    if (!_client) {
      _client = generateClient<Schema>();
    }
    return (_client as Record<string, unknown>)[prop as string];
  },
});

/**
 * Public API Key authenticated client for public reads
 * Use this for unauthenticated/demo user access to public data.
 * Uses 'apiKey' authentication mode for simple public access without IAM credentials.
 * This works with models that have `allow.publicApiKey()` authorization.
 * Lazily initialized to ensure Amplify is configured first.
 *
 * Usage:
 *   import { publicClient } from '@/services/amplifyClient';
 *   const { data } = await publicClient.models.Top10Opportunity.list();
 */
export const publicClient = new Proxy({} as ReturnType<typeof generateClient<Schema>>, {
  get(_target, prop) {
    if (!_publicClient) {
      _publicClient = generateClient<Schema>({
        authMode: 'apiKey',
      });
    }
    return (_publicClient as Record<string, unknown>)[prop as string];
  },
});

export type { Schema };
